import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApplicationConfig } from '@models/app/application-config.model';
import { FunctionsHelper } from '@shared/utils/helpers/functions.helper';
import { ServerErrors } from '@shared/utils/helpers/siq-errors.helper';
import { SiqPopupHelper } from '@shared/utils/helpers/siq-popup.helper';
import { catchError, map, ObjectUnsubscribedError, Observable, throwError, timeout, TimeoutError } from 'rxjs';
import { EnvironmentsService } from './environments.service';
import { MixpanelService } from './mixpanel.service';
import { SplashScreenService } from './splash-screen.service';

@Injectable({
  providedIn: 'root'
})
export class SiqApiService {

  isShownAlert = false;

  constructor(
    private httpClient: HttpClient,
    private environmentsService: EnvironmentsService,
    private mixpanelService: MixpanelService,
    private siqPopupHelper: SiqPopupHelper,
    private splashScreenService: SplashScreenService) { }

  /*
    @param requestType can be siq or nsiq(native siq request)
  */

  makeSiqRequest<T, P extends object>(endpoint: string, requestType: string, params: P, type: string, skipAuth: boolean, responseType = 'json'): Observable<T | HttpErrorResponse> {
    const APIEndpoint = this.environmentsService.APIURL + '/rest/';
    let headers;


    headers = new HttpHeaders({
      'X-App-Version': '5.3.30',
      'Accept-Version': '5.3.30',
      'X-Use-Auth': requestType,
      'X-App-Platform': 'web'
    });

    if (skipAuth) {
      headers = headers.append('X-Skip-Interceptor', '');
    }

    if (!navigator.onLine) {
      window.alert('No Internet connection. Please try again later.');
      throw throwError(() => FunctionsHelper.createGenericError(503));
    }

    const urlParams = this.getUrlParams(params, type);

    const url = APIEndpoint + endpoint + urlParams;

    const options: object = {
      body: params,
      headers,
      withCredentials: true,
      responseType
    };

    return this.httpClient.request<T>(type, url, options).pipe(
      timeout({ each: 30000 }),
      catchError((initialError: HttpErrorResponse) => {
        return this.catchSiqError(initialError, endpoint, url, requestType);
      })
    );
  }

  makeCognitoRequest<T, P extends object>(type: string, params: P, skipAuth: boolean): Observable<T | HttpErrorResponse> {
    const APIEndpoint = this.environmentsService.COGNITOURL + '/v1/token';
    const ClientID = this.environmentsService.CLIENTID;
    let headers;

    headers = new HttpHeaders({
      'X-Use-Auth': 'cognito'
    });

    if (skipAuth) {
      headers = headers.append('X-Skip-Interceptor', '');
    }

    if (!navigator.onLine) {
      window.alert('No Internet connection. Please try again later.');
      throw throwError(() => FunctionsHelper.createGenericError(503));
    }

    const options = {
      body: !skipAuth ? { ...params } : { ClientID, ...params },
      headers,
    };

    return this.httpClient.request<T>(type, APIEndpoint, options).pipe(
      map((data: T) => data),
      catchError((initialError: HttpErrorResponse) => {
        return this.catchSiqError(initialError, '/v1/token', APIEndpoint, 'cognito');
      })
    );
  }

  makeEdpRequest<T, P extends object>(endpoint: string, params: P, type: string): Observable<T | HttpErrorResponse> {
    const APIEndpoint = this.environmentsService.EDPURL + '/';

    const headers = new HttpHeaders({
      'X-App-Version': '5.3.30',
      'Accept-Version': '5.3.30',
      'Ocp-Apim-Subscription-Key': this.environmentsService.EDPSUBSRCIPTION,
      'X-Use-Auth': 'edp',
      'X-App-Platform': 'web'
    });

    const urlParams = this.getUrlParams(params, type);
    const options = {
      body: params,
      headers
    };

    const url = `${APIEndpoint}${endpoint}${urlParams}`;

    return this.httpClient.request<T>(type, url, options).pipe(
      timeout({ each: 30000 }),
      catchError((initialError: HttpErrorResponse) => {
        return this.catchSiqError(initialError, endpoint, url, 'edp');
      })
    );
  }

  makeTextRequest(endpoint: string): Observable<string | HttpErrorResponse> {
    const url = this.environmentsService.APIURL + '/rest/' + endpoint + '?dataType=text';

    const headers = new HttpHeaders({
      'X-App-Version': '5.3.30',
      'Accept-Version': '5.3.30',
      'X-Skip-Interceptor': '',
      'X-Use-Auth': 'siq',
      'X-App-Platform': 'web'
    });

    return this.httpClient.get(url, { headers, responseType: 'text' }).pipe(
      timeout({ each: 30000 }),
      catchError((initialError: HttpErrorResponse) => {
        return this.catchSiqError(initialError, endpoint, url, 'siq');
      })
    );
  }

  makeSleepNumberRequest<T, P extends object>(endpoint: string, params: P, type: string): Observable<T | HttpErrorResponse> {
    const APIEndpoint = this.environmentsService.SLEEPNUMBERURL + '/api/storefront/';

    const headers = new HttpHeaders({
      'X-Use-Auth': 'sn'
    });

    if (!navigator.onLine) {
      window.alert('No Internet connection. Please try again later.');
      throw throwError(() => FunctionsHelper.createGenericError(503));
    }

    const urlParams = this.getUrlParams(params, type);
    //eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const url = APIEndpoint + endpoint + urlParams;
    const options = {
      body: params,
      headers,
    };

    return this.httpClient.request<T>(type, url, options).pipe(
      timeout({ each: 30000 }),
      map(data => data),
      catchError((initialError: HttpErrorResponse) => {
        return this.catchSiqError(initialError, endpoint, url, 'sn');
      })
    );
  }

  getConfig(): Observable<ApplicationConfig> {
    return this.makeSiqRequest<ApplicationConfig, object>('sn/v3/configs/app', 'siq', {}, 'GET', true).pipe(
      map((response: ApplicationConfig | HttpErrorResponse) => {
        if (response instanceof HttpErrorResponse) {
          throw response;
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  //#region Private functions

  private getUrlParams(params: object, type: string): string {
    let urlParams = new HttpParams();
    if (type === 'GET') {
      for (const key in params) {
        if (Object.hasOwnProperty.call(params, key)) {
          urlParams = urlParams.set(key, (params as Record<string, string | number | boolean>)[key]);
        }
      }
    }
    if (urlParams.toString() === '') {
      return urlParams.toString();
    }
    return '?' + urlParams.toString();
  }

  private catchSiqError(initialError: HttpErrorResponse, endpoint: string, url: string, originFrom: string): Observable<HttpErrorResponse> {
    this.splashScreenService.setAuthLoading(false);
    this.splashScreenService.setBedsLoading(false);
    this.splashScreenService.setSessionLoading(false);
    if (initialError instanceof TimeoutError && !this.filterSpecificEndpointsError(endpoint)) {
      this.mixpanelService.trackError(url, 0, 'Timeout', 0);
      this.siqPopupHelper.showAlert('API Service',  ServerErrors.ApiErrors.requestTimeout.text, ServerErrors.ApiErrors.requestTimeout.title);
      return throwError(() => FunctionsHelper.createGenericError(-504, `App Timeout for ${endpoint}`));
    } else if (!(initialError instanceof ObjectUnsubscribedError)) {
      switch (originFrom) {
        case 'siq':
        case 'nsiq':
          this.mixpanelService.trackError(url, initialError.error.Error?.Code, initialError.error.Error?.Message, initialError.status);
          break;
        case 'cognito':
          this.mixpanelService.trackError(url, initialError.error.data.code, initialError.error.data.message, initialError.status);
          break;
        case 'sn':
          this.mixpanelService.trackError(url, 0, '', initialError.status);
          break;
        case 'edp':
          this.mixpanelService.trackError(url, initialError.error.code, initialError.error.message, initialError.status);
          break;
      }
      this.handleAPIError(initialError, endpoint);
    }
    return throwError(() => initialError);
  }

  private handleAPIError(initialError: HttpErrorResponse, endpoint: string): void {
    if (this.filterSpecificEndpointsError(endpoint)) {
      return;
    }
    if (initialError.status >= 500) {
      this.siqPopupHelper.showAlert('API Service', ServerErrors.ServerMaintenance.text, ServerErrors.ServerMaintenance.title);
    }
  }

  private filterSpecificEndpointsError(endpoint: string): boolean {
    return endpoint.includes('license') || endpoint.includes('privacy-policy');
  }
  //#endregion
}
